import * as AppConstants from "AppConstants";
import axios from "axios";
import {
  getUrlForAddActivityForMember,
  getUrlForBulkAddActivity,
  getUrlForAllActivitiesForMemberByDate,
  getUrlToDeleteLeavePlan,
  getUrlToFetchSingleActivity,
  getUrlToUpdateSingleActivity,
} from "utils/endpointUtils/activitiesEndpointUtil";
import {
  getUrlForAddLeavePlanForMember,
  getUrlForAllLeavePlansForMemberByDate,
  getUrlForUpdateLeavePlanForMember,
  getUrlForLeavePlanById,
} from "utils/endpointUtils/leavePlansEndpointUtil";
import {
  getUrlForAllMembersForClient,
  getUrlForAllMembersForAllClients,
} from "utils/endpointUtils/clientEndpointUtil";
import {
  getUrlForMemberForUser,
  getUrlToCreateMember,
  getUrlToUpdateMember,
  getUrlToDeleteMember,
  getUrlForAllMemberReportings,
  getUrlForMembersPage,
  getUrlForMemberReportingsPage,
  getUrlToCreateMemberReporting,
  getUrlToUpdateMemberReporting,
  getUrlToDeleteMemberReporting,
  getUrlForLoadEffortSummary,
} from "utils/endpointUtils/memberEndpointUtil";
import { setAuthHeader } from "app/services/auth.header.service";
import { readSessionToken } from "./app.state.service";
import { track } from "analytics/usageTracker";
import {
  FEATURE_EXPORT_CONCISE,
  FEATURE_EXPORT_CONCISE_MONTHLY,
  FEATURE_EXPORT_CONSOLIDATED,
  FEATURE_EXPORT_DETAILED,
  FEATURE_EXPORT_UTILIZATION,
} from "analytics/featureCatalog";

export const loadActivitiesFromBackendForMember = async (memberId, fromDate, toDate) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForAllActivitiesForMemberByDate(memberId, fromDate, toDate);

  return await axios.get(url, config);
};

export const loadLeavePlansFromBackendForMember = async (memberId, fromDate, toDate) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForAllLeavePlansForMemberByDate(memberId, fromDate, toDate);

  return await axios.get(url, config);
};

/**
 * Returns promise by calling "POST /v1/api/activities/save " endpoint
 * to save activity for a member
 * @param {*} activityObject - activity object to be saved
 * @returns promise which returns response of save activity endpoint
 */
export const addActivityToBackend = async (activityObject) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const postUrl = getUrlForAddActivityForMember();

  return await axios.post(postUrl, activityObject, config);
};

/**
 * Saves a batch of activities in one request.
 *
 * The batch is accepted or refused as a whole, so the caller surfaces the server's message rather
 * than retrying: a 422 means a rule was broken, not that the call failed.
 *
 * @since NEXT_VERSION
 */
export const addActivitiesToBackend = async (bulkActivityObject) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const postUrl = getUrlForBulkAddActivity();

  return await axios.post(postUrl, bulkActivityObject, config);
};

export const getLeavePlanById = async (leaveId) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForLeavePlanById(leaveId);

  return await axios.get(url, config);
};

export const addLeavePlanToBackend = async (leavePlanObject) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const postUrl = getUrlForAddLeavePlanForMember();

  return await axios.post(postUrl, leavePlanObject, config);
};

export const updateLeavePlanToBackend = async (leaveId, leavePlanObject) => {
  const config = setAuthHeader();

  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const postUrl = getUrlForUpdateLeavePlanForMember(leaveId);

  return await axios.put(postUrl, leavePlanObject, config);
};

export const deleteLeavePlan = async (leaveId) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlToDeleteLeavePlan(leaveId);

  return await axios.delete(url, config);
};

/**
 * Returns promise by calling "GET v1/api/clients/{clientId}/members" endpoint
 * to get all members associated with client with id clientId
 * @param {*} clientId for which members to be retrieved
 * @returns promise which returns list of members
 */
export const loadMembersFromBackendForClient = async (clientId) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForAllMembersForClient(clientId);

  return await axios.get(url, config);
};

/**
 * Downloads a timesheet for either id space in either format, replacing four near-identical
 * handlers that differed only along those two axes.
 *
 * ownerType is explicit rather than inferred: member_id and manager_id come from independent
 * sequences whose values overlap, so an id alone does not say whose timesheet is wanted.
 *
 * The period stays month + year — the workbook layout is month-bound (one weekly sheet per week
 * within the month, plus a monthly sheet), so a date range would change the format.
 *
 * @since NEXT_VERSION
 */
export const downloadTimesheetHandler = async (
  memberId,
  ownerType,
  selectedYear,
  selectedMonth,
  legacyFormat = false,
  clientIds = null
) => {
  const token = readSessionToken();
  if (!token) {
    return null;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    responseType: "blob",
  };

  const params = new URLSearchParams({
    memberId: String(memberId),
    ownerType,
    year: String(selectedYear),
    month: String(selectedMonth),
  });
  if (legacyFormat) params.append("legacyFormat", "true");
  // Omitted for "All": the export then covers every client the person works at, matching the
  // consolidated report. A specific client narrows it to that one.
  const ids = Array.isArray(clientIds) ? clientIds.filter((id) => id != null && id !== "") : [];
  if (ids.length > 0) params.append("clientIds", ids.join(","));

  const response = await axios.get(`${AppConstants.GET_DOWNLOAD_TIMESHEET_ENDPOINT}?${params.toString()}`, config);
  // Counted here, after the download, so every screen that exports is counted once and a failure is not.
  track(legacyFormat ? FEATURE_EXPORT_CONCISE : FEATURE_EXPORT_DETAILED);
  return response;
};

/**
 * Downloads the consolidated timesheet: one workbook for one client covering every member in the
 * given manager scope, each member a column group.
 *
 * Takes a manager scope rather than an owner — the ticket asks for "every applicable member within
 * the selected(manager) scope", so the server resolves who that is from the same query the
 * consolidated report uses. clientId is required and singular: the layout has no Account column, so
 * one workbook covers one client.
 *
 * @since NEXT_VERSION
 */
export const downloadConsolidatedTimesheetHandler = async (
  managerIds,
  clientId,
  selectedYear,
  selectedMonth
) => {
  const token = readSessionToken();
  if (!token) {
    return null;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    responseType: "blob",
  };

  const ids = Array.isArray(managerIds) ? managerIds.filter((id) => id != null && id !== "") : [];
  const params = new URLSearchParams({
    managerIds: ids.join(","),
    clientId: String(clientId),
    year: String(selectedYear),
    month: String(selectedMonth),
  });

  const response = await axios.get(
    `${AppConstants.GET_DOWNLOAD_CONSOLIDATED_TIMESHEET_ENDPOINT}?${params.toString()}`,
    config
  );
  track(FEATURE_EXPORT_CONSOLIDATED);
  return response;
};

/**
 * Downloads the Concise Monthly (finance) workbook for one person.
 *
 * Owner-driven, unlike the consolidated export: HRMS-267 covers a single resource, so the server
 * derives the client from that person's own record rather than taking a clientId here. ownerType is
 * sent for the same reason the per-person export sends it — the member and manager id spaces overlap.
 *
 * @since NEXT_VERSION
 */
export const downloadConciseMonthlyHandler = async (
  memberId,
  ownerType,
  selectedYear,
  selectedMonth,
  clientIds = null
) => {
  const token = readSessionToken();
  if (!token) {
    return null;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    responseType: "blob",
  };

  const params = new URLSearchParams({
    memberId: String(memberId),
    ownerType: String(ownerType),
    year: String(selectedYear),
    month: String(selectedMonth),
  });
  // Omitted entirely when the Client filter reads "All": the server then covers every client the
  // person works at, which is what makes the report span clients.
  const ids = Array.isArray(clientIds) ? clientIds.filter((id) => id != null && id !== "") : [];
  if (ids.length > 0) {
    params.set("clientIds", ids.join(","));
  }

  const response = await axios.get(
    `${AppConstants.GET_DOWNLOAD_CONCISE_MONTHLY_ENDPOINT}?${params.toString()}`,
    config
  );
  track(FEATURE_EXPORT_CONCISE_MONTHLY);
  return response;
};

/**
 * Downloads the org-level Utilization Report (HRMS-319) for one month.
 *
 * Covers every person at every client, or one department's people when `departmentCode` is given.
 * The server names the file and sends it in Content-Disposition.
 *
 * @since NEXT_VERSION
 */
export const downloadUtilizationReportHandler = async (
  selectedYear,
  selectedMonth,
  departmentCode = null
) => {
  const token = readSessionToken();
  if (!token) {
    return null;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    responseType: "blob",
  };

  const params = new URLSearchParams({
    year: String(selectedYear),
    month: String(selectedMonth),
  });
  // Omitted for "All": the server then reports on the whole organisation.
  if (departmentCode) {
    params.set("departmentCode", departmentCode);
  }

  const response = await axios.get(
    `${AppConstants.GET_DOWNLOAD_UTILIZATION_REPORT_ENDPOINT}?${params.toString()}`,
    config
  );
  track(FEATURE_EXPORT_UTILIZATION);
  return response;
};

export const fetchSingleActivity = async (activityId) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlToFetchSingleActivity(activityId);

  return await axios.get(url, config);
};

export const updateMemberSingleActivity = async (activityId, activityObj) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlToUpdateSingleActivity(activityId);
  return await axios.put(url, activityObj, config);
};

/**
 * Returns promise by calling "GET v1/api/members/all" endpoint
 * to get all members associated with all clients
 * @returns promise which returns list of members
 */
export const loadMembersForAllClientsFromBackend = async (clientId = 0) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForAllMembersForAllClients();
  return await axios.get(url, config);
};

/**
 * Returns promise by calling "GET v1/api/members/byuser/{userId}" endpoint
 * @param {*} userId for which memberId to be fetched from backend
 * @returns promise which returns member id for user
 */
export const getMemberByUserId = async (userId) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForMemberForUser(userId);
  return await axios.get(url, config);
};

export const addMemberToBackend = async (data) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToCreateMember();
  return await axios.post(url, data, config);
};

export const deleteMember = async (member_id) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlToDeleteMember(member_id);

  return await axios.delete(url, config);
};

export const updateMemberToBackend = async (id, data) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToUpdateMember(id);
  return await axios.put(url, data, config);
};

// Server-driven page fetch for the Master Data Member grid. Returns the PageResponse envelope.
export const loadMembersPageFromBackend = async ({ page, pageSize, sortBy, sortDirection, searchQuery }) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForMembersPage({ page, pageSize, sortBy, sortDirection, searchQuery });
  return await axios.get(url, config);
};

// Server-driven page fetch for the Master Data Member-Reporting grid. Returns the PageResponse envelope.
export const loadMemberReportingsPageFromBackend = async ({ page, pageSize, sortBy, sortDirection, searchQuery }) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlForMemberReportingsPage({ page, pageSize, sortBy, sortDirection, searchQuery });
  return await axios.get(url, config);
};

export const loadMemberReportingsFromBackend = async () => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForAllMemberReportings();
  return await axios.get(url, config);
};


export const loadEffortSummary = async (startDate, endDate, clientIds) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlForLoadEffortSummary(startDate, endDate, clientIds);
  return await axios.get(url, config);
};

export const addMemberReportingToBackend = async (data) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToCreateMemberReporting();
  return await axios.post(url, data, config);
};

export const deleteMemberReporting = async (member_id) => {
  const config = setAuthHeader();

  if (config === null) {
    throw new Error("Invalid Auth Header or Token Expired");
  }

  const url = getUrlToDeleteMemberReporting(member_id);

  return await axios.delete(url, config);
};

export const updateMemberReportingToBackend = async (id, data) => {
  const config = setAuthHeader();
  if (config === null) throw new Error("Invalid Auth Header or Token Expired");

  const url = getUrlToUpdateMemberReporting(id);
  return await axios.put(url, data, config);
};

/**
 * Alias for loadMembersForAllClientsFromBackend - used by ProjectAdminView
 */
export const fetchAllMembers = loadMembersForAllClientsFromBackend;

/**
 * Alias for loadMembersForAllClientsFromBackend - used by SkillMatrixManagerView
 */
export const loadAllMembers = loadMembersForAllClientsFromBackend;

